<script setup>
import { ref, watch, onMounted, onUnmounted, nextTick } from 'vue';
import { useAccount } from 'dashboard/composables/useAccount';
import { useUISettings } from 'dashboard/composables/useUISettings';
import suiteAPI from 'dashboard/api/integrations/suite';

const POSITION_KEY = 'suite-softphone-position';

const { accountId } = useAccount();
const { uiSettings } = useUISettings();

const softphoneUrl = ref('');
const enabled = ref(false);
const iframeRef = ref(null);
let hostHandle = null;

// WayHub (themeHelper) aplica `.dark` no body, não no <html>
const isDark = () =>
  document.body.classList.contains('dark') ||
  document.documentElement.classList.contains('dark');

const unmountHost = () => {
  if (hostHandle && typeof hostHandle.unmount === 'function') {
    hostHandle.unmount();
  }
  hostHandle = null;
};

/** Base do Suite (origem + path, ex.: https://plat5.../suite) a partir da URL do softphone. */
const suiteAssetBase = url => {
  try {
    const u = new URL(url);
    const dir = u.pathname.replace(/\/[^/]*$/, '');
    return `${u.origin}${dir}`;
  } catch {
    return String(url || '')
      .replace(/[?#].*$/, '')
      .replace(/\/[^/]*$/, '');
  }
};

const hasSavedPosition = () => {
  try {
    const parsed = JSON.parse(window.localStorage.getItem(POSITION_KEY) || '');
    if (
      Number.isFinite(Number(parsed?.left)) &&
      Number.isFinite(Number(parsed?.top))
    ) {
      return true;
    }
    return (
      (Number.isFinite(Number(parsed?.collapsed?.left)) &&
        Number.isFinite(Number(parsed?.collapsed?.top))) ||
      (Number.isFinite(Number(parsed?.expanded?.left)) &&
        Number.isFinite(Number(parsed?.expanded?.top)))
    );
  } catch {
    return false;
  }
};

/** Aba fechada: longe do botão de enviar. Aberta: canto inferior sobre o painel. */
const defaultPosition = () => {
  if (uiSettings.value?.is_contact_sidebar_open) {
    return { right: 16, bottom: 16 };
  }
  return { right: 16, top: 88 };
};

const applyFallbackPosition = iframe => {
  if (!iframe || hasSavedPosition()) return;
  const pos = defaultPosition();
  iframe.style.left = 'auto';
  iframe.style.right = `${pos.right}px`;
  if (pos.top != null) {
    iframe.style.bottom = 'auto';
    iframe.style.top = `${pos.top}px`;
    return;
  }
  iframe.style.top = 'auto';
  iframe.style.bottom = `${pos.bottom}px`;
};

const applyDefaultPosition = iframe => {
  if (!iframe || hasSavedPosition()) return;
  const api = window.SuiteSoftphoneHost;
  if (typeof api?.applyDefaultPosition === 'function') {
    api.applyDefaultPosition(iframe, defaultPosition());
    return;
  }
  applyFallbackPosition(iframe);
};

/**
 * WayHub seta color-scheme: dark no <html>. Sem override no iframe, o Chromium
 * pinta o canvas do frame (faixa branca/opaca) atrás do softphone transparente.
 */
const applyIframeTransparency = iframe => {
  if (!iframe) return;
  iframe.style.colorScheme = 'normal';
  iframe.style.backgroundColor = 'transparent';
  iframe.style.background = 'transparent';
};

/** Carrega o mesmo softphone_host_iframe.js do Suite (fonte única do host). */
const loadHostScript = suiteBaseUrl => {
  if (window.SuiteSoftphoneHost?.mount) {
    return Promise.resolve(window.SuiteSoftphoneHost);
  }

  const base = suiteAssetBase(suiteBaseUrl);
  const src = `${base}/js/softphone/softphone_host_iframe.js`;

  return new Promise((resolve, reject) => {
    const existing = document.querySelector(
      'script[data-suite-softphone-host="1"]'
    );
    if (existing) {
      if (window.SuiteSoftphoneHost?.mount) {
        resolve(window.SuiteSoftphoneHost);
        return;
      }
      existing.addEventListener('load', () =>
        resolve(window.SuiteSoftphoneHost)
      );
      existing.addEventListener('error', reject);
      return;
    }
    const s = document.createElement('script');
    s.src = src;
    s.async = true;
    s.setAttribute('data-suite-softphone-host', '1');
    s.onload = () => resolve(window.SuiteSoftphoneHost);
    s.onerror = () =>
      reject(new Error('Falha ao carregar softphone_host_iframe.js'));
    document.head.appendChild(s);
  });
};

const onIframeLoad = async () => {
  if (!iframeRef.value || !softphoneUrl.value) return;
  applyIframeTransparency(iframeRef.value);
  try {
    const api = await loadHostScript(softphoneUrl.value);
    if (!api?.mount) return;
    unmountHost();
    hostHandle = api.mount({
      iframe: iframeRef.value,
      softphoneUrl: softphoneUrl.value,
      position: defaultPosition(),
    });
    // Host script seta background, mas não color-scheme — reforça após mount.
    applyIframeTransparency(iframeRef.value);
    applyDefaultPosition(iframeRef.value);
  } catch {
    /* softphone segue no iframe; sem resize do host */
  }
};

const loadSoftphone = async () => {
  unmountHost();
  if (!accountId.value) {
    enabled.value = false;
    softphoneUrl.value = '';
    return;
  }

  try {
    const { data } = await suiteAPI.softphone({ dark: isDark() });
    enabled.value = Boolean(data.enabled && data.url);
    softphoneUrl.value = data.url || '';
    if (enabled.value && softphoneUrl.value) {
      await nextTick();
      applyIframeTransparency(iframeRef.value);
      loadHostScript(softphoneUrl.value).catch(() => {});
    }
  } catch {
    enabled.value = false;
    softphoneUrl.value = '';
  }
};

watch(accountId, () => {
  loadSoftphone();
});

watch(
  () => uiSettings.value?.is_contact_sidebar_open,
  () => {
    applyDefaultPosition(iframeRef.value);
  }
);

onMounted(() => {
  loadSoftphone();
});

onUnmounted(() => {
  unmountHost();
});
</script>

<template>
  <Teleport to="body">
    <iframe
      v-if="enabled && softphoneUrl"
      id="stk-telefone-container"
      ref="iframeRef"
      :src="softphoneUrl"
      name="Ramal Web"
      allow="microphone"
      class="bg-transparent scheme-normal"
      @load="onIframeLoad"
    />
  </Teleport>
</template>
